// src/app/services/social-auth.service.ts
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { GoogleSignIn } from '@capawesome/capacitor-google-sign-in';
import { FacebookLogin } from '@capacitor-community/facebook-login';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// Apple Sign-In interfaces
interface AppleSignInResult {
	user: string;
	email?: string;
	fullName?: string;
	givenName?: string;
	familyName?: string;
	identityToken?: string;
	authorizationCode?: string;
	state?: string;
}

// Web Apple Sign-In response interface
interface AppleWebSignInResponse {
	authorization: {
		user?: string;
		id_token: string;
		code: string;
		state?: string;
	};
	user?: {
		email?: string;
		name?: {
			firstName?: string;
			lastName?: string;
		};
	};
}

export interface SocialUser {
	id: string;
	email: string;
	name: string;
	photoUrl?: string;
	provider: 'google-oauth2' | 'google' | 'facebook' | 'apple';
	accessToken?: string;
	idToken?: string;
}

export interface SocialAuthResult {
	socialUser: SocialUser;
	authTokens: any;
}

export interface AuthState {
	isAuthenticated: boolean;
	user: SocialUser | null;
	loading: boolean;
	error: string | null;
}

@Injectable({
	providedIn: 'root',
})
export class SocialService {
	private authState = new BehaviorSubject<AuthState>({
		isAuthenticated: false,
		user: null,
		loading: false,
		error: null,
	});

	public authState$ = this.authState.asObservable();

	constructor(private http: HttpClient) {
		this.initializeAuth();
	}

	private async initializeAuth() {
		try {
			// @capawesome/capacitor-google-sign-in requires the web client ID on all platforms.
			// The iOS client ID is configured separately in Info.plist (GIDClientID key).
			await GoogleSignIn.initialize({
				clientId: environment.google.clientIds.web,
				scopes: [
					'https://www.googleapis.com/auth/userinfo.profile',
					'https://www.googleapis.com/auth/userinfo.email',
				],
			});

			// Initialize Facebook Login for all platforms
			await FacebookLogin.initialize({
				appId: environment.facebook.appId,
				autoLogAppEvents: true,
				xfbml: true,
				version: 'v13.0',
			});

			// Check if we're on a native platform and log Facebook app availability
			if (Capacitor.isNativePlatform()) {
				try {
					const status = await FacebookLogin.getCurrentAccessToken();
					console.log('Facebook current access token:', status);
				} catch (error) {
					console.log('Facebook access token check error:', error);
				}
			}

			// Apple Sign-In is initialized automatically by the native plugin
			// Web Apple Sign-In is initialized via the JavaScript SDK in index.html
		} catch (error) {
			console.error('Social auth initialization failed:', error);
		}
	}

	private getProviderCredentials(provider: string): { clientId: string; clientSecret: string } {
		switch (provider) {
			case 'google':
			case 'google-oauth2':
				return {
					clientId: environment.google.clientIds.web,
					clientSecret: environment.google.clientSecret,
				};
			case 'facebook':
				return {
					clientId: environment.facebook.appId,
					clientSecret: environment.facebook.clientToken,
				};
			case 'apple':
				// Apple Sign-In may need different credentials structure
				// Adjust based on your backend requirements
				return {
					clientId: this.getAppleClientId(),
					clientSecret: '', // Apple uses different auth flow
				};
			default:
				throw new Error(`Unsupported provider: ${provider}`);
		}
	}

	private updateAuthState(updates: Partial<AuthState>) {
		this.authState.next({
			...this.authState.value,
			...updates,
		});
	}

	// Apple Sign In (iOS native + Web)
	async signInWithApple(): Promise<SocialAuthResult | null> {
		try {
			this.updateAuthState({ loading: true, error: null });

			const platform = Capacitor.getPlatform();
			let result: any;

			if (platform === 'ios') {
				// Use native iOS plugin
				result = await this.signInWithAppleNative();
			} else {
				// Use web Apple Sign-In
				result = await this.signInWithAppleWeb();
			}

			if (result) {
				const user: SocialUser = {
					id: result.user || result.id || 'apple_user_' + Date.now(),
					email: result.email || '',
					name: result.name || result.fullName || result.givenName || 'Apple User',
					provider: 'apple',
					idToken: result.identityToken || result.id_token,
				};

				this.updateAuthState({
					isAuthenticated: true,
					user,
					loading: false,
				});

				const backendResponse = await this.convertTokenWithBackend(user);
				return { socialUser: user, authTokens: backendResponse };
			}

			return null;
		} catch (error) {
			this.updateAuthState({
				loading: false,
				error: `Apple Sign-In failed: ${error}`,
			});
			throw error;
		}
	}

	// Native iOS Apple Sign-In
	private async signInWithAppleNative(): Promise<AppleSignInResult> {
		try {
			const { registerPlugin } = await import('@capacitor/core');
			const AppleSignInPlugin = registerPlugin<any>('AppleSignInPlugin');
			const result: AppleSignInResult = await AppleSignInPlugin.signIn();
			return result;
		} catch (error) {
			throw new Error(`Native Apple Sign-In failed: ${error}`);
		}
	}

	// Web Apple Sign-In implementation
	private async signInWithAppleWeb(): Promise<any> {
		return new Promise((resolve, reject) => {
			// Check if Apple Sign-In JavaScript SDK is loaded
			if (!(window as any).AppleID) {
				reject('Apple Sign-In not loaded. Make sure to include the Apple JS SDK in your index.html');
				return;
			}

			// Initialize Apple ID (in case not done automatically)
			try {
				(window as any).AppleID.auth.init({
					clientId: this.getAppleClientId(),
					scope: 'name email',
					redirectURI: this.getAppleRedirectUri(),
					usePopup: true, // Use popup instead of redirect for better UX
				});
			} catch {
				// Already initialized (or the SDK rejected a duplicate init) - safe to continue.
			}

			// Perform sign in
			(window as any).AppleID.auth
				.signIn()
				.then((response: AppleWebSignInResponse) => {
					if (response.authorization) {
						const result = {
							user: response.authorization.user || 'apple_user_' + Date.now(),
							email: response.user?.email,
							name: response.user?.name ? `${response.user.name.firstName || ''} ${response.user.name.lastName || ''}`.trim() : 'Apple User',
							id_token: response.authorization.id_token,
							code: response.authorization.code,
							state: response.authorization.state,
						};

						// Verify state if it was sent
						const expectedState = document.querySelector('meta[name="appleid-signin-state"]')?.getAttribute('content');
						if (expectedState && response.authorization.state !== expectedState) {
							reject('Invalid state parameter - possible CSRF attack');
							return;
						}

						resolve(result);
					} else {
						reject('No authorization received from Apple');
					}
				})
				.catch((error: any) => {
					if (error.error === 'popup_closed_by_user') {
						reject('Sign-in cancelled by user');
					} else {
						reject(`Apple Sign-In failed: ${error.error || error}`);
					}
				});
		});
	}

	// Apple Services ID used as the web client_id. This is a single value across
	// environments and must match the Services ID configured in the Apple Developer portal.
	private getAppleClientId(): string {
		return 'com.ponyplace.io.web';
	}

	// Get Apple redirect URI.
	// Apple requires this to exactly match a registered Return URL on the Services ID.
	// We register the bare origin per environment (e.g. https://beta.ponyplace.net,
	// https://ponyplace.net), so return the current origin with no path.
	private getAppleRedirectUri(): string {
		return window.location.origin;
	}

	// Apple Sign-In is available natively on iOS and via the JS SDK on web.
	// Hidden on Android native, which has no Apple Sign-In support.
	isAppleSignInAvailable(): boolean {
		const platform = Capacitor.getPlatform();
		return platform === 'ios' || platform === 'web';
	}

	// Hidden on iOS until Sign in with Apple is offered alongside Google (App Store requirement).
	isGoogleSignInAvailable(): boolean {
		return Capacitor.getPlatform() !== 'ios';
	}

	// Google Sign In
	async signInWithGoogle(): Promise<SocialAuthResult | null> {
		try {
			this.updateAuthState({ loading: true, error: null });

			const result = await GoogleSignIn.signIn();
			console.log('Google sign-in result:', result);

			if (result) {
				const user: SocialUser = {
					id: result.userId,
					email: result.email,
					name: result.displayName,
					photoUrl: result.imageUrl,
					provider: 'google',
					accessToken: result.accessToken,
					idToken: result.idToken,
				};

				this.updateAuthState({
					isAuthenticated: true,
					user,
					loading: false,
				});

				// Send to your backend for token conversion
				const backendResponse = await this.convertTokenWithBackend(user);
				return { socialUser: user, authTokens: backendResponse };
			}
			return null;
		} catch (error) {
			this.updateAuthState({
				loading: false,
				error: `Google sign-in failed: ${error}`,
			});
			throw error;
		}
	}

	// Facebook Sign In
	async signInWithFacebook(): Promise<SocialAuthResult | null> {
		try {
			this.updateAuthState({ loading: true, error: null });

			const permissions = ['email', 'public_profile'];
			console.log('Requesting Facebook permissions:', permissions);
			console.log('Platform:', Capacitor.getPlatform());
			console.log('Is native platform:', Capacitor.isNativePlatform());

			// Use the native Capacitor FacebookLogin plugin
			const result = await FacebookLogin.login({ permissions });
			console.log('Facebook sign-in result:', result);

			if (result && result.accessToken) {
				// Get user profile using Facebook Graph API
				const profile = await this.getFacebookProfile(result.accessToken.token);

				const user: SocialUser = {
					id: result.accessToken.userId,
					email: profile.email,
					name: profile.name,
					photoUrl: profile.picture?.data?.url,
					provider: 'facebook',
					accessToken: result.accessToken.token,
				};

				this.updateAuthState({
					isAuthenticated: true,
					user,
					loading: false,
				});

				const backendResponse = await this.convertTokenWithBackend(user);
				return { socialUser: user, authTokens: backendResponse };
			}
			return null;
		} catch (error) {
			this.updateAuthState({
				loading: false,
				error: `Facebook sign-in failed: ${error}`,
			});
			throw error;
		}
	}

	private async getFacebookProfile(token: string): Promise<any> {
		const url = `https://graph.facebook.com/me?fields=id,name,email,picture&access_token=${token}`;
		return this.http.get(url).toPromise();
	}

	// Backend token conversion
	private async convertTokenWithBackend(user: SocialUser): Promise<any> {
		// Normalize provider name to the backend's social auth identifiers.
		let backendProvider: string = user.provider;
		if (user.provider === 'google') {
			backendProvider = 'google-oauth2';
		} else if (user.provider === 'apple') {
			backendProvider = 'apple-id';
		}

		// Build request body using PonyPlace OAuth credentials
		const body = {
			grant_type: 'convert_token',
			client_id: environment.server_oauth_client_id,
			client_secret: environment.server_oauth_client_secret,
			backend: backendProvider,
			token: user.accessToken || user.idToken || '',
		};

		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});

		const response = await this.http
			.post(`${environment.base_api_url}/auth/oauth/convert-token/`, body, {
				headers: headers,
			})
			.toPromise();

		// Transform OAuth response to match Token format expected by AuthService
		// Backend returns: { access_token, refresh_token, expires_in, token_type, scope }
		// AuthService expects: { access, refresh, token_type }
		if (response && (response as any).access_token && (response as any).refresh_token) {
			return {
				access: (response as any).access_token,
				refresh: (response as any).refresh_token,
				token_type: (response as any).token_type,
			};
		}

		return response;
	}

	// Sign out
	async signOut(): Promise<void> {
		try {
			const currentUser = this.authState.value.user;

			if (currentUser) {
				switch (currentUser.provider) {
					case 'google':
						await GoogleSignIn.signOut();
						break;
					case 'facebook':
						await FacebookLogin.logout();
						break;
					case 'apple':
						// Apple doesn't require explicit sign out
						// The user stays signed in at the system level
						break;
				}
			}

			this.updateAuthState({
				isAuthenticated: false,
				user: null,
				loading: false,
				error: null,
			});
		} catch (error) {
			this.updateAuthState({
				error: `Sign out failed: ${error}`,
			});
			throw error;
		}
	}

	// Get current user
	getCurrentUser(): SocialUser | null {
		return this.authState.value.user;
	}

	// Check if user is authenticated
	isAuthenticated(): boolean {
		return this.authState.value.isAuthenticated;
	}

	// Check authentication status on app startup
	// Note: Google session restoration is handled by AuthService.checkStorageForToken()
	// which manages backend tokens. The @capawesome plugin has no client-side refresh API.
	async checkAuthStatus(): Promise<void> {
		try {
			// Check Facebook status using native Capacitor plugin
			if (Capacitor.isNativePlatform()) {
				try {
					const fbStatus = await FacebookLogin.getCurrentAccessToken();
					if (fbStatus && fbStatus.accessToken) {
						const profile = await this.getFacebookProfile(fbStatus.accessToken.token);
						const user: SocialUser = {
							id: fbStatus.accessToken.userId,
							email: profile.email,
							name: profile.name,
							photoUrl: profile.picture?.data?.url,
							provider: 'facebook',
							accessToken: fbStatus.accessToken.token,
						};
						this.updateAuthState({
							isAuthenticated: true,
							user,
						});
					}
				} catch (fbError) {
					console.log('No Facebook session found');
				}
			}

			// Note: Apple Sign-In doesn't have a "check status" method
			// Users need to sign in fresh each time, or implement token storage
		} catch (error) {
			console.log('No existing auth session found');
		}
	}
}
